import type { AppProps } from 'next/app'
import { ScreenClassProvider, setConfiguration } from 'react-grid-system'
import { useRouter } from 'next/router'
import { fireAnalyticsEvent, getGtmClickSource } from '~analyticsUtils'
import '../styles/variables.scss'
import breakpoints from '../styles/breakpoints.json'
import '../styles/globals.scss'
import { useEffect, useRef, useState } from 'react'
import { skipGtmPageview } from '~analyticsUtils'
import { INDButton } from '~elements'

// Configure react-grid-system
type BreakpointKey = keyof typeof breakpoints

const breakpointVars = Object.keys(breakpoints).map(key => parseInt(breakpoints[key as BreakpointKey].toString()))

const containerWidths = Object.entries(breakpoints).map(([key, value]) => {
  if (key === 'xs' || key === 'sm' || key === 'sm-minus') {
    return '100%'
  }

  // Subtract 48px for gutters
  return value - 48
})

setConfiguration({
  breakpointVars,
  containerWidths,
  gutterWidth: 32,
  maxScreenClass: 'xl'
})

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const [previousPage, setPreviousPage] = useState('')
  const hasFiredInitialPageview = useRef(false)

  const handleRouteChangeStart = () => {
    setPreviousPage(router.asPath)
  }

  const handleRouteChangeComplete = (url: string) => {
    if (skipGtmPageview.current) {
      skipGtmPageview.current = false
      return
    }

    // Prevent double firing on first mount
    if (!hasFiredInitialPageview.current) {
      hasFiredInitialPageview.current = true
    } else if (url === router.asPath) {
      return
    }

    const gtmPageViewData = {
      event: 'pageview',
      clickPreviousPv: previousPage.includes('shows') ? 'shows' : 'other_events',
      eventData: {
        eventType: 'other_events'
      },
      pageType: getGtmClickSource(url)
    }

    fireAnalyticsEvent(null, url, gtmPageViewData)
  }

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    // Fire pageview on first mount when router is ready
    if (router.isReady && !hasFiredInitialPageview.current) {
      handleRouteChangeComplete(router.asPath)
    }

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router.isReady, previousPage])

  return (
    <ScreenClassProvider>
      <INDButton />
      <div id='root-modal' />
      <Component {...pageProps} />
      <div style={{ display: 'none' }}>build number {process.env.NEXT_PUBLIC_BUILD_NUMBER}</div>
    </ScreenClassProvider>
  )
}

export default App
